/** biome-ignore-all lint/suspicious/noExplicitAny: any has to be used */
/** biome-ignore-all lint/complexity/noBannedTypes: Function has to be used */
import {
  type Context,
  context as otContext,
  type Span,
  SpanStatusCode,
} from '@opentelemetry/api';
import { logs, type SeverityNumber } from '@opentelemetry/api-logs';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { type ExportResult, ExportResultCode } from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchLogRecordProcessor,
  LoggerProvider,
  type LogRecordExporter,
  type ReadableLogRecord,
} from '@opentelemetry/sdk-logs';
import {
  type MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics';
import {
  BatchSpanProcessor,
  type ReadableSpan,
  type SpanExporter,
} from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { API_BASE, OTLP_COLLECTOR_BASE } from '@/env';

const SERVICE_NAME = 'front';

/**
 * A custom SpanExporter that acts as a proxy.
 * It buffers spans until a real exporter is asynchronously loaded.
 */
class LazyLoadingTraceExporter implements SpanExporter {
  private bufferedSpans: ReadableSpan[] = [];
  private realExporter: SpanExporter | null = null;

  /**
   * Called by the BatchSpanProcessor to export spans.
   * If the real exporter is loaded, it forwards the spans.
   * Otherwise, it buffers them.
   */
  export(
    spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void,
  ): void {
    if (this.realExporter) {
      this.realExporter.export(spans, resultCallback);
    } else {
      this.bufferedSpans.push(...spans);
      resultCallback({ code: ExportResultCode.SUCCESS });
    }
  }

  shutdown(): Promise<void> {
    if (this.realExporter) {
      return this.realExporter.shutdown();
    }
    this.bufferedSpans = [];
    return Promise.resolve();
  }

  /**
   * Asynchronously loads the real OTLP exporter, initializes it,
   * and sends any buffered spans.
   */
  async load(): Promise<void> {
    if (this.realExporter) return;

    const { OTLPTraceExporter } = await import(
      '@opentelemetry/exporter-trace-otlp-proto'
    );

    this.realExporter = new OTLPTraceExporter({
      url: `${OTLP_COLLECTOR_BASE}/v1/traces`,
    });

    // If there are spans buffered during the loading time, export them now.
    if (this.bufferedSpans.length > 0) {
      this.realExporter.export(this.bufferedSpans, () => {});
      this.bufferedSpans = [];
    }
  }
}

class LazyLoadingLogExporter implements LogRecordExporter {
  private bufferedLogs: ReadableLogRecord[] = [];
  private realExporter: LogRecordExporter | null = null;

  export(
    logs: ReadableLogRecord[],
    resultCallback: (result: ExportResult) => void,
  ): void {
    if (this.realExporter) {
      this.realExporter.export(logs, resultCallback);
    } else {
      this.bufferedLogs.push(...logs);
      resultCallback({ code: ExportResultCode.SUCCESS });
    }
  }

  shutdown(): Promise<void> {
    if (this.realExporter) {
      return this.realExporter.shutdown();
    }
    this.bufferedLogs = [];
    return Promise.resolve();
  }

  async load(): Promise<void> {
    if (this.realExporter) return;
    const { OTLPLogExporter } = await import(
      '@opentelemetry/exporter-logs-otlp-proto'
    );
    this.realExporter = new OTLPLogExporter({
      url: `${OTLP_COLLECTOR_BASE}/v1/logs`,
    });
    if (this.bufferedLogs.length > 0) {
      this.realExporter.export(this.bufferedLogs, () => {});
      this.bufferedLogs = [];
    }
  }
}

const lazyTraceExporter = new LazyLoadingTraceExporter();

const lazyLogExporter = new LazyLoadingLogExporter();

const resource = resourceFromAttributes({
  'service.name': SERVICE_NAME,
});

const tracerProvider = new WebTracerProvider({
  resource: resource,
  spanProcessors: [new BatchSpanProcessor(lazyTraceExporter)],
});

const loggerProvider = new LoggerProvider({
  resource: resource,
  processors: [new BatchLogRecordProcessor(lazyLogExporter)],
});

export function initializeOtelCore() {
  // Register the provider with the ZoneContextManager. THIS MUST BE FIRST.
  tracerProvider.register({
    contextManager: new ZoneContextManager(),
  });

  logs.setGlobalLoggerProvider(loggerProvider);

  const propagateTraceHeaderCorsUrls = [new RegExp(`${API_BASE}/.*`)];
  const ignoreUrls = [new RegExp(`^(?!${API_BASE})`)];

  // Register instrumentations to start collecting traces immediately.
  // Spans will be collected in memory until an exporter is attached.
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({ propagateTraceHeaderCorsUrls, ignoreUrls }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls,
        ignoreUrls,
      }),
    ],
  });
}

export function loadOtelExporters() {
  lazyTraceExporter.load();
  lazyLogExporter.load();
}

export function getTracer() {
  return tracerProvider.getTracer(SERVICE_NAME);
}

let meterProviderPromise: Promise<MeterProvider> | null = null;

/**
 * An async function that initializes the MeterProvider on the first call.
 * Subsequent calls will return the existing promise.
 */
async function initializeMeterProvider(): Promise<MeterProvider> {
  // Dynamically import the heavy dependencies only when needed
  const { OTLPMetricExporter } = await import(
    '@opentelemetry/exporter-metrics-otlp-proto'
  );
  const { MeterProvider } = await import('@opentelemetry/sdk-metrics');

  const metricExporter = new OTLPMetricExporter({
    url: `${OTLP_COLLECTOR_BASE}/v1/metrics`,
    headers: {},
    concurrencyLimit: 1,
  });

  const metricReader = new PeriodicExportingMetricReader({
    exporter: metricExporter,
    exportIntervalMillis: 10000,
    exportTimeoutMillis: 5000,
  });

  const provider = new MeterProvider({
    resource: resource,
    readers: [metricReader],
  });

  return provider;
}

export async function getMeter() {
  if (!meterProviderPromise) {
    meterProviderPromise = initializeMeterProvider();
  }
  const provider = await meterProviderPromise;
  return provider.getMeter(SERVICE_NAME);
}

export async function traceAsync<T>(
  name: string,
  fn: () => Promise<T>,
): Promise<T> {
  return getTracer().startActiveSpan(name, (span) =>
    fn()
      .then((result) => {
        span.setStatus({ code: SpanStatusCode.OK });
        return result;
      })
      .catch((err) => {
        span.recordException(err);
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: err.message,
        });
        throw err;
      })
      .finally(() => {
        span.end();
      }),
  );
}

export async function instrumentAsync<Return>(
  spanName: string,
  fn: (span: Span) => Promise<Return>,
  context?: Context,
): Promise<Return> {
  if (!context) {
    context = otContext.active();
  }

  return getTracer().startActiveSpan(spanName, {}, context, async (s: Span) => {
    try {
      const res = await fn(s);

      s.setStatus({ code: SpanStatusCode.OK });

      return res;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      s.recordException(error);
      s.setStatus({
        code: SpanStatusCode.ERROR,
        message: error.message,
      });
      throw err;
    } finally {
      s.end();
    }
  }) as Return;
}

export function instrument<Return>(
  spanName: string,
  fn: (span: Span) => Return,
  context?: Context,
): Return {
  if (!context) {
    context = otContext.active();
  }

  return getTracer().startActiveSpan(spanName, {}, context, (s: Span) => {
    try {
      const res = fn(s);

      s.setStatus({ code: SpanStatusCode.OK });

      return res;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      s.recordException(error);
      s.setStatus({
        code: SpanStatusCode.ERROR,
        message: error.message,
      });
      throw err;
    } finally {
      s.end();
    }
  }) as Return;
}

/**
 * Type guard to check if a value is a promise-like object ("thenable").
 * @param value The value to check.
 * @returns True if the value has a .then function, false otherwise.
 */
function isPromise<T>(value: any): value is Promise<T> {
  return value && typeof value.then === 'function';
}

/**
 * Wraps a class method with OpenTelemetry tracing logic.
 * This helper function contains the shared logic used by all decorators.
 *
 * @param originalMethod The class method to be wrapped.
 * @param getSpanName A function that generates the span name using the class instance.
 * @returns A new function that includes the tracing logic.
 */
function wrapMethodWithTrace<This, Args extends any[], Return>(
  originalMethod: (this: This, ...args: Args) => Return,
  spanName: string,
): (this: This, ...args: Args) => Return {
  return function (this: This, ...args: Args): Return {
    const tracer = getTracer();

    return tracer.startActiveSpan(spanName, (span) => {
      try {
        const result = originalMethod.apply(this, args);

        if (isPromise(result)) {
          // --- ASYNCHRONOUS METHOD ---
          return result
            .then((res) => {
              span.setStatus({ code: SpanStatusCode.OK });
              return res;
            })
            .catch((err: unknown) => {
              const error = err instanceof Error ? err : new Error(String(err));
              span.recordException(error);
              span.setStatus({
                code: SpanStatusCode.ERROR,
                message: error.message,
              });
              throw err;
            })
            .finally(() => {
              span.end();
            });
        } else {
          // --- SYNCHRONOUS METHOD ---
          span.setStatus({ code: SpanStatusCode.OK });
          span.end();
          return result;
        }
      } catch (err: unknown) {
        // --- SYNCHRONOUS ERROR ---
        const error = err instanceof Error ? err : new Error(String(err));
        span.recordException(error);
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error.message,
        });
        span.end();
        throw err;
      }
    }) as Return;
  };
}

/**
 * Decorator factory for creating method tracers.
 * This captures the class name upfront to avoid minification issues.
 * @param className The original name of the class as a string.
 * @returns A method decorator.
 */
export function createTracer(className: string) {
  return function traceMethod<This, Args extends any[], Return>(
    originalMethod: (this: This, ...args: Args) => Return,
    context: ClassMethodDecoratorContext<
      This,
      (this: This, ...args: Args) => Return
    >,
  ) {
    const methodName = String(context.name);
    // The span name is created with the original, un-minified class name
    const spanName = `${className}.${methodName}`;
    return wrapMethodWithTrace(originalMethod, spanName);
  };
}

/**
 * Wraps a standalone async function with OpenTelemetry tracing.
 *
 * @param spanName The name for the span (e.g., 'Api.getCruiseDetail').
 * @param fn The async function to be traced.
 * @returns A new function that is instrumented with tracing.
 */
export function trace<Args extends any[], Return>(
  spanName: string,
  fn: (...args: Args) => Return,
): (...args: Args) => Return {
  // We can reuse your perfectly good wrapper logic.
  // The only difference is we pass `null` for `this` since there is no class instance.
  const wrappedFn = wrapMethodWithTrace(fn, spanName);

  // Return a function that calls the wrapped one with the correct `this` context (or lack thereof).
  return (...args: Args): Return => wrappedFn.apply(null, args);
}

export function getLogger() {
  return logs.getLogger(SERVICE_NAME);
}

/**
 * A simplified logging function to emit log records.
 * @param severity The severity level of the log (e.g., SeverityNumber.INFO).
 * @param message The main log message.
 * @param attributes Optional key-value pairs for structured logging.
 */
export function log(
  severity: SeverityNumber,
  message: string,
  attributes?: Record<string, any>,
) {
  getLogger().emit({
    severityNumber: severity,
    body: message,
    attributes,
  });
}
